import { api } from "api/task";
import { assigneeApi } from "api/assignee";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useMedia } from "shared/providers";

import Drawer from "react-modern-drawer";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, CircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { DetailTab, DetailTabRef } from "./DetailTab";
import { ActivityTab } from "./ActivityTab";
import { Tabs } from "shared/components/Tabs";
import { buildTaskPayload, TaskTab } from "../constants";

import { TitleWithTooltip } from "./TitleWithTooltip";

import { extractEntitiesFromJson } from "shared/app/Notes/helpers";
import { EditorRefApi } from "shared/components/Editor";
import { TaskDrawerSkeleton } from "./TaskDrawerSkeleton";
import { getErrorMessage } from "utils/error";
import {
  RichDoc,
  DetailTabData,
  TitleResponse,
  TaskSubjectPayload,
  Subject,
  CreateTaskPayload
} from "../types";

type Mode = "create" | "view";

type TaskDrawerProps = {
  open: boolean;
  onClose: () => void;
  mode: Mode;
  taskId?: number;
  noteId?: number;
  onCreated?: () => void;
  defaultSubject?: Subject;
  readOnly?: boolean;
};

export const TaskDrawer = ({
  open,
  onClose,
  mode = "create",
  taskId,
  noteId,
  onCreated,
  defaultSubject,
  readOnly = false
}: TaskDrawerProps) => {
  const { isMobile } = useMedia();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState<TaskTab>(TaskTab.DETAILS);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [titleWithEntites, setTitleWithEntities] = useState<RichDoc | null>(
    null
  );
  const [originalTitle, setOriginalTitle] = useState<TitleResponse | null>(
    null
  );
  const [originalDescription, setOriginalDescription] =
    useState<RichDoc | null>(null);
  const [currentDetailData, setCurrentDetailData] =
    useState<DetailTabData | null>(null);
  const [changedInView, setChangedInView] = useState(false);
  const [createModeKey, setCreateModeKey] = useState(0);
  const titleEditorRef = useRef<EditorRefApi>(null);
  const detailTabRef = useRef<DetailTabRef>(null);
  const { data: assigneesData } = useQuery({
    queryKey: ["ASSIGNEES"],
    queryFn: () => assigneeApi.getAssignees(),
    enabled: open
  });
  const assignees = assigneesData ?? [];
  const { data: task, isLoading: isTaskLoading } = useQuery({
    queryKey: ["TASK", taskId],
    queryFn: () => api.getTask(taskId!),
    enabled: open && mode === "view" && Boolean(taskId)
  });

  const { data: activityLogsData, isLoading: isActivityLogsLoading } = useQuery(
    {
      queryKey: ["TASK_ACTIVITY_LOGS", taskId],
      queryFn: () => api.getTaskActivityLogs(taskId!).then((res) => res.items),
      enabled: open && mode === "view" && Boolean(taskId)
    }
  );
  const { data: mentionedActivityLogs } = useQuery({
    queryKey: ["TASK_MENTION_ACTIVITY_LOGS", taskId],
    queryFn: () => api.getMentionNotes(taskId!),
    enabled: open && mode === "view" && Boolean(taskId)
  });
  const { mutate: createTask, isPending: isCreating } = useMutation({
    mutationKey: ["CREATE_TASK"],
    mutationFn: (payload: CreateTaskPayload) => api.createTask(payload),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["TASKS_TABLE"] });
      toast.success("Task created successfully");
      onCreated?.();
      handleDrawerClose();
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      toast.error(message);
    }
  });

  const { mutate: updateTask, isPending: isUpdating } = useMutation({
    mutationKey: ["UPDATE_TASK"],
    mutationFn: (payload: { id: number; data: Partial<CreateTaskPayload> }) =>
      api.updateTask(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["TASKS_TABLE"] });
      queryClient.invalidateQueries({ queryKey: ["TASK", taskId] });
      toast.success("Task updated successfully");
      handleDrawerClose();
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      toast.error(message);
    }
  });

  const saving = isCreating || isUpdating;

  useEffect(() => {
    if (mode === "create" && open) {
      setTitleWithEntities(null);
      setOriginalTitle(null);
      setOriginalDescription(null);
      setCurrentDetailData(
        defaultSubject ? ({ subject: defaultSubject } as DetailTabData) : null
      );
      setChangedInView(false);
      setCurrentTab(TaskTab.DETAILS);
      setCreateModeKey((prev) => prev + 1);
    }
  }, [mode, open, defaultSubject]);

  useEffect(() => {
    if (mode === "view" && open && Boolean(noteId))
      setCurrentTab(TaskTab.ACTIVITY);
  }, [mode, open, noteId]);

  const subjectKey = currentDetailData?.subject
    ? `${currentDetailData.subject.type}:${currentDetailData.subject.id}`
    : null;
  const previousSubjectKeyRef = useRef<string | null>(subjectKey);
  useEffect(() => {
    if (previousSubjectKeyRef.current === subjectKey) return;
    previousSubjectKeyRef.current = subjectKey;
    titleEditorRef.current?.blur();
    detailTabRef.current?.blurDescriptionEditor();
  }, [subjectKey]);

  useEffect(() => {
    if (!task) return;
    const { title, description, ...detailData } = task;
    setOriginalTitle(title);
    setOriginalDescription(description);
    setTitleWithEntities(
      (prev) =>
        ({
          ...prev,
          document_text: title.document_text,
          document_html: title.document_html,
          entities: []
        }) as RichDoc
    );
    setCurrentDetailData({
      ...detailData,
      description: description
        ? { ...description, entities: [] }
        : { document_text: "", document_html: "", entities: [] }
    });
  }, [task]);

  const handleDetailDataChange = (updatedData: DetailTabData) => {
    setCurrentDetailData(updatedData);
    setChangedInView(true);
  };

  const handleSaveTask = () => {
    if (!titleWithEntites?.document_text.trim()) {
      toast.error("Task title is required");
      return;
    }
    const titleChanged =
      !originalTitle ||
      titleWithEntites.document_text !== originalTitle.document_text;
    const descriptionChanged =
      !originalDescription ||
      currentDetailData?.description?.document_text !==
        originalDescription.document_text;
    if (titleChanged) {
      const payload = buildTaskPayload(
        titleWithEntites,
        currentDetailData,
        titleWithEntites.entities
      );
      if (mode === "create") createTask(payload);
      else updateTask({ id: taskId!, data: payload });
    } else {
      const getRelatedToPayload = () => {
        if (currentDetailData?.subject)
          return {
            related_to: {
              subject_type:
                currentDetailData.subject.type === "boom_screen_applications"
                  ? TaskSubjectPayload.BOOM_SCREEN_APPLICATIONS
                  : TaskSubjectPayload.CRM_LEADS,
              subject_id: currentDetailData.subject.id
            }
          };

        if (currentDetailData?.subject === null) return { related_to: {} };

        return {};
      };

      const payload = {
        ...(descriptionChanged &&
          currentDetailData?.description && {
            description: currentDetailData.description
          }),
        ...(currentDetailData?.due_date && {
          due_date: currentDetailData.due_date
        }),
        ...getRelatedToPayload(),
        ...(currentDetailData?.assignees && {
          assignee_ids: currentDetailData.assignees.map(({ id }) => id)
        })
      };
      updateTask({ id: taskId!, data: payload });
    }
  };

  const handleTitleChange = (json: object, html: string) => {
    const text = titleEditorRef.current?.getDocument().text || "";
    const entities = extractEntitiesFromJson(json);
    setTitleWithEntities({
      document_text: text,
      document_html: html,
      entities
    } as RichDoc);

    setChangedInView(true);
  };
  const handleDrawerClose = () => {
    titleEditorRef.current?.blur();
    detailTabRef.current?.blurDescriptionEditor();

    setIsTitleEditing(false);
    onClose();
  };

  if (open && mode === "view" && isTaskLoading)
    return (
      <TaskDrawerSkeleton
        open={open}
        onClose={handleDrawerClose}
        isMobile={isMobile}
      />
    );
  return (
    <Drawer
      className="bg-background-1000 border-alpha-100 rounded-l-lg border"
      direction="right"
      size={isMobile ? 380 : 520}
      open={open}
      onClose={handleDrawerClose}
      lockBackgroundScroll
    >
      <div
        key={mode === "create" ? `create-${createModeKey}` : `view-${taskId}`}
        className="flex h-screen flex-col"
      >
        <div className="sticky top-0 z-50 border-b border-alpha-80 px-6 py-4">
          <div
            className={cx(
              "space-y-2 w-full max-w-[448px] transition-all overflow-hidden",
              isTitleEditing ? "max-h-none" : "max-h-[80px]"
            )}
          >
            <p className="text-content-400 font-medium text-base">Task</p>
            <div className="flex items-start gap-2">
              <div className="h-[24px] w-[24px] flex justify-center items-center">
                <HugeiconsIcon
                  icon={CircleIcon}
                  className="text-content-300"
                  size={20}
                />
              </div>
              <TitleWithTooltip
                ref={titleEditorRef}
                titleText={titleWithEntites?.document_text}
                titleHtml={titleWithEntites?.document_html}
                editable={!readOnly}
                onChange={handleTitleChange}
                placeholder="Add task title. Use @ to mention someone..."
                withViewOnly={mode === "view" && !changedInView}
                onEditModeChange={setIsTitleEditing}
                propertyId={currentDetailData?.subject?.property?.id}
                propertyName={currentDetailData?.subject?.property?.name}
                requirePropertyAccess
                hasRelatedObject={!!currentDetailData?.subject}
                mentionUsers={assignees}
              />
            </div>
          </div>
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.LG}
            HugeIcon={Cancel01Icon}
            onClick={handleDrawerClose}
            aria-label="Close drawer"
            className="absolute right-6 top-8"
          />
        </div>
        <div className="border-b border-alpha-40 px-6 pt-6 sticky">
          <Tabs
            setActiveTab={(e) => setCurrentTab(e)}
            selected={currentTab}
            tabs={[TaskTab.DETAILS, TaskTab.ACTIVITY]}
            variant="underline"
            tabProps={{
              Details: {
                disabled: false
              },
              Activity: {
                disabled: mode === "create"
              }
            }}
          />
        </div>
        <Show if={currentTab === TaskTab.DETAILS}>
          <DetailTab
            ref={detailTabRef}
            mode={mode}
            assignees={assignees}
            data={currentDetailData}
            onDataChange={handleDetailDataChange}
            readOnly={readOnly}
          />
        </Show>
        <Show if={currentTab === TaskTab.ACTIVITY}>
          <ActivityTab
            activityLogs={activityLogsData || []}
            mentionedActivityLogs={mentionedActivityLogs}
            activityLogsLoading={isActivityLogsLoading}
            taskId={taskId!}
            noteId={noteId}
            readOnly={readOnly}
          />
        </Show>
        <Show if={currentTab === TaskTab.DETAILS}>
          <div className="border-t border-alpha-100 px-6 py-4">
            <div className="flex justify-end gap-2">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.BASE}
                className="w-[125px] shrink-0"
                onClick={handleDrawerClose}
              >
                {mode === "view" ? "Close" : "Cancel"}
              </Button>
              <Show if={(mode === "create" || changedInView) && !readOnly}>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.BASE}
                  className="w-[125px] shrink-0"
                  onClick={handleSaveTask}
                  disabled={saving || isTaskLoading}
                  loading={saving}
                >
                  Save
                </Button>
              </Show>
            </div>
          </div>
        </Show>
      </div>
    </Drawer>
  );
};
